import { GetServerSidePropsContext } from 'next';
import { useContext, useMemo } from 'react';
import CustomErrorComponent from 'src/components/custom-error/custom-error.component';
import PageESIWrapper from 'src/components/page-esi-wrapper/page-esi-wrapper.component';
import PageHead from 'src/components/page-head/page-head.component';
import PageRenderer from 'src/components/page-renderer/page-renderer.component';
import PreviewBannerComponent from 'src/components/preview-banner/preview.banner-component';
import { resolveHreflangAvailability } from 'src/utils/hreflang-availability-resolver';
import { hardRedirectTo } from 'src/utils/navigation';
import { getCriticalImagePreloads } from 'src/utils/performance';
import ssrHelper, {
  isPreviewMode,
  PageData,
  SSR_STATUS_CODES,
} from 'src/utils/ssr-helper';
import styles from 'styles/home.module.less';
import { VAAContext } from './_app';

// Build index pages only when not in AEM preview mode

export const getStaticProps = async ({ locale }: GetServerSidePropsContext) => {
  if (process.env.NEXT_PUBLIC_AEM_MODE === 'preview') {
    return {
      notFound: true,
    };
  }

  // const revalidate = 60 * 60 * 24 * 30; // ~30 days
  const { REVALIDATE_SECONDS } = require('../revalidate-config.cjs');
  let revalidate = REVALIDATE_SECONDS; // 60 seconds

  if (isPreviewMode) {
    revalidate = 5;
  }

  const pageData = await ssrHelper('/', locale);

  if (
    pageData?.status === SSR_STATUS_CODES.ERROR &&
    pageData?.errorCode === 404
  ) {
    return {
      notFound: true,
      revalidate,
    };
  }

  if (pageData?.status === SSR_STATUS_CODES.SUCCESS && pageData?.pageHeader) {
    pageData.pageHeader.hreflangPath = '/';
    const { locales } = await resolveHreflangAvailability('/');
    pageData.pageHeader.hreflangLocales = locales;
  }
  return {
    props: {
      ...pageData,
      regenerateTime: new Date().toString(),
    },
    revalidate,
  };
};

export default function Page({
  status,
  errorCode,
  errorMessage,
  pageHeader,
  content,
  localeInfo,
  regenerateTime,
}: PageData) {
  const vaaContext = useContext(VAAContext);
  const criticalImagePreloads = useMemo(
    () => getCriticalImagePreloads(content),
    [content],
  );

  if (pageHeader) {
    pageHeader.isHomepage = true;
    if (localeInfo) {
      pageHeader.localeInfo = localeInfo;
    }
  }

  if (status === 'error')
    return (
      <>
        {pageHeader && <PageHead {...pageHeader} />}
        <CustomErrorComponent
          errorCode={errorCode}
          errorMessage={errorMessage}
        />
      </>
    );

  if (status === SSR_STATUS_CODES.REDIRECT) {
    hardRedirectTo(content);
  }

  return (
    <>
      {pageHeader && (
        <PageHead
          {...pageHeader}
          regenerateTime={regenerateTime}
          criticalImagePreloads={criticalImagePreloads}
        />
      )}
      <PageESIWrapper
        searchPanelConfig={pageHeader?.searchPanelConfig}
        localeInfo={localeInfo}>
        {isPreviewMode && <PreviewBannerComponent />}
        <div className={styles[`theme-${vaaContext.theme}`]}>
          <PageRenderer items={content} />
        </div>
      </PageESIWrapper>
    </>
  );
}
